import { useMemo } from 'react';
import { ASSETS } from '../config/site';
import { useLocale } from '../context/LocaleContext';
import LogoLoop from '../components/reactbits/LogoLoop/LogoLoop';
import { FAQSection, FinalCTA, PageHero, StepsSection } from '../components/shared/PageSections';
import { CheckIcon } from '../components/ui/Icons';
import { ButtonLink, SectionIntro } from '../components/ui/Primitives';

function CompanyHeroVisual({ content }) {
  return (
    <div className="candidate-deck" aria-label={content.fit}>
      <span className="candidate-deck__count">{content.applications}</span>
      <article className="candidate-photo candidate-photo--back">
        <span>5/6</span><img src={ASSETS.comicGirl} alt="" />
      </article>
      <article className="candidate-photo candidate-photo--front">
        <span>4/4</span><img src={ASSETS.comicBoy} alt="" />
      </article>
      <div className="candidate-requirements glass-card">
        <strong>{content.fit}</strong>
        {content.requirements.map((item) => <span key={item}><i><CheckIcon /></i>{item}</span>)}
      </div>
    </div>
  );
}

function CompanyFeatures({ content }) {
  return (
    <section className="company-features">
      <SectionIntro eyebrow={content.eyebrow} title={content.title} />
      <div className="company-features__grid" data-reveal="stagger">
        {content.items.map((item, index) => (
          <article key={item.title} style={{ '--stagger': index }}>
            <div><span>{String(index + 1).padStart(2, '0')}</span><i>↗</i></div>
            <h3>{item.title}</h3><p>{item.description}</p>
          </article>
        ))}
      </div>
    </section>
  );
}

function RecruitingSection({ content }) {
  return (
    <section className="recruiting-section section-shell">
      <SectionIntro eyebrow={content.eyebrow} title={content.title}>
        <ButtonLink>{content.cta}</ButtonLink>
      </SectionIntro>
      <div className="recruiting-board" data-reveal="image">
        <img src={ASSETS.templatePlatform} alt="" />
        <div className="recruiting-board__status glass-card">
          <small>{content.items[1].title}</small>
          <strong>{content.statusTitle}</strong>
          <div>{content.statusLabels.map((status, index) => <span key={status.label} style={{ '--status': index }}>{status.label} <b>{status.count}</b></span>)}</div>
        </div>
      </div>
      <div className="recruiting-benefits" data-reveal="stagger">
        {content.items.map((item, index) => <article key={item.title} style={{ '--stagger': index }}><span>0{index + 1}</span><h3>{item.title}</h3><p>{item.description}</p></article>)}
      </div>
    </section>
  );
}

function AudienceSection({ content }) {
  return (
    <section className="audience-section section-shell">
      <SectionIntro eyebrow={content.eyebrow} title={content.title} align="center" />
      <div className="audience-grid" data-reveal="stagger">
        {content.items.map((item, index) => (
          <article key={item.title} style={{ '--stagger': index }}>
            <span className="audience-grid__index">0{index + 1}</span>
            <h3>{item.title}</h3><p>{item.description}</p>
            <ButtonLink variant="outline">{content.cta}</ButtonLink>
          </article>
        ))}
      </div>
    </section>
  );
}

export default function CompaniesPage() {
  const { t } = useLocale();
  const companies = t.companies;
  const partnerLogos = useMemo(
    () => ['YourApplicant', 'UBS', 'Coca-Cola', 'Midor', 'Atlantis'].map((title) => ({
      node: <span className="partner-wordmark">{title}</span>,
      title,
      ariaLabel: title,
    })),
    [],
  );

  return (
    <main id="main-content">
      <PageHero
        eyebrow={companies.hero.eyebrow}
        title={companies.hero.title}
        description={companies.hero.subtitle}
        primaryLabel={companies.hero.primaryCta}
        secondaryLabel={companies.hero.secondaryCta}
        secondaryTo="https://calendly.com/your-applicant/ausbildungsbetriebe"
        visual={<CompanyHeroVisual content={companies.hero} />}
        className="page-hero--companies"
      />

      <div className="partner-loop" data-reveal="fade">
        <LogoLoop
          logos={partnerLogos}
          speed={70}
          direction="left"
          logoHeight={34}
          gap={68}
          hoverSpeed={18}
          scaleOnHover
          fadeOut
          fadeOutColor="#f8f6f1"
          ariaLabel={companies.features.eyebrow}
        />
      </div>

      <CompanyFeatures content={companies.features} />
      <RecruitingSection content={companies.recruiting} />
      <AudienceSection content={companies.audiences} />
      <StepsSection
        eyebrow={companies.steps.eyebrow}
        title={companies.steps.title}
        items={companies.steps.items.map((item) => ({ title: item.title, text: item.description }))}
        primaryLabel={t.common.actions.register}
        secondaryLabel={t.common.actions.viewPricing}
      />
      <FAQSection eyebrow={companies.faq.eyebrow} title={companies.faq.title} items={companies.faq.items} />
      <FinalCTA eyebrow={t.home.closing.eyebrow} title={t.home.closing.title} text={t.home.closing.description} buttonLabel={t.home.closing.cta} />
    </main>
  );
}
